// src/CustomerForm.js
import "./App.css";
import React, { useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { setDoc, doc } from "firebase/firestore";
import { db, storage } from "./firebase";

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png"
});

const DEFAULT_LITHO_IMAGE_URL = process.env.PUBLIC_URL + "/default-litho-image.png";

// ChangeMapCenter: pans the map to the location (without changing zoom)
function ChangeMapCenter({ location }) {
  const map = useMap();
  React.useEffect(() => {
    if (location) map.panTo(location);
  }, [location, map]);
  return null;
}

// LocationSelector: updates the location when the map is clicked
function LocationSelector({ setLocation }) {
  useMapEvents({
    click(e) {
      setLocation(e.latlng);
    },
  });
  return null;
}

// DetectLocationControl: a control displayed inside the map (bottom left)
function DetectLocationControl({ setLocation }) {
  const map = useMap();
  const handleDetectLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const detectedLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setLocation(detectedLocation);
          map.setView(detectedLocation, 15);
        },
        (error) => {
          console.error("Error detecting location:", error);
          alert("Unable to retrieve your location. Please check your browser settings.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };
  return (
    <div
      className="detect-location-control"
      style={{
        position: "absolute",
        bottom: "10px",
        left: "10px",
        zIndex: 1000,
        pointerEvents: "auto",
      }}
    >
      <button
        onClick={handleDetectLocation}
        style={{
          padding: "10px",
          background: "none",
          color: "#007BFF",
          border: "none",
          borderRadius: "50%",
          cursor: "pointer",
          fontSize: "1.5rem",
          lineHeight: "1",
        }}
      >
        📌
      </button>
    </div>
  );
}

const CustomerForm = () => {
  // Store neutral state values for dropdowns
  const [colorOption, setColorOption] = useState("Mono"); // "Mono" or "Multi"
  const [frameColor, setFrameColor] = useState("Black");   // "Black" or "White"

  // Other state variables
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(DEFAULT_LITHO_IMAGE_URL);
  const fileInputRef = useRef(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [location, setLocation] = useState(null);
  const [errors, setErrors] = useState({
    name: false,
    phone: false,
    address: false,
    photo: false,
    location: false,
  });
  const [language, setLanguage] = useState("en");
  const [isSubmitting, setIsSubmitting] = useState(false); // Loading state

  // Labels and translations
  const labels = {
    en: {
      uploadPhoto: "Upload Your Photo",
      pageTitle: "Order Your Custom Lithophane",
      colorOption: "Color Option",
      frameColor: "Frame Color",
      pinLocation: "Drop a pin or detect your location:",
      name: "Name",
      phone: "Phone",
      address: "Address",
      note: "Note",
      submit: "Submit Order",
      placeholderName: "Enter your name",
      placeholderPhone: "Enter your phone number",
      placeholderAddress: "Enter your address",
      placeholderNote: "Enter any extra instructions (optional)",
      colorOptions: { Mono: "Mono", Multi: "Multi" },
      frameColorOptions: { Black: "Black", White: "White" },
    },
    ar: {
      uploadPhoto: "ارفع صورتك",
      pageTitle: "اطلب صورتك ثلاثية الأبعاد",
      colorOption: "خيار اللون",
      frameColor: "لون الإطار",
      pinLocation: "ضع دبوس أو اكتشف موقعك:",
      name: "الاسم",
      phone: "الهاتف",
      address: "العنوان",
      note: "ملاحظة",
      submit: "إرسال الطلب",
      placeholderName: "أدخل اسمك",
      placeholderPhone: "أدخل رقم هاتفك",
      placeholderAddress: "أدخل عنوانك",
      placeholderNote: "أدخل أي تعليمات إضافية (اختياري)",
      colorOptions: { Mono: "لون واحد", Multi: "الوان متعددة" },
      frameColorOptions: { Black: "اسود", White: "ابيض" },
    },
  };

  // File upload actions
  const handleImageClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    setPhoto(file);
    if (file) {
      setPhotoPreview(URL.createObjectURL(file));
    } else {
      setPhotoPreview(DEFAULT_LITHO_IMAGE_URL);
    }
  };

  // Form submission: validate, show a waiting state, then submit and reset form
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate required fields
    const newErrors = {
      name: name.trim() === "",
      phone: phone.trim() === "",
      address: address.trim() === "",
      photo: photo === null,
      location: location === null,
    };
    setErrors(newErrors);
    if (Object.values(newErrors).some((error) => error)) {
      return; // Red borders indicate errors
    }
    // Set loading state and disable submission
    setIsSubmitting(true);
    try {
      let fileURL = DEFAULT_LITHO_IMAGE_URL;
      if (photo) {
        const fileName = new Date().getTime() + "_" + photo.name;
        const storageRef = ref(storage, `uploads/${fileName}`);
        await uploadBytes(storageRef, photo);
        fileURL = await getDownloadURL(storageRef);
      }
      const locationData = location ? { lat: location.lat, lng: location.lng } : null;
      const customId = name.trim().replace(/\s+/g, "_") + "_" + new Date().getTime();
      await setDoc(doc(db, "orders", customId), {
        name,
        phone,
        address,
        note,
        location: locationData,
        fileURL,
        colorOption,
        frameColor,
        orderStatus: "pending",
        createdAt: new Date(),
      });
      alert("Your order has been submitted successfully!");
      // Reset form fields
      setName("");
      setPhone("");
      setAddress("");
      setNote("");
      setPhoto(null);
      setPhotoPreview(DEFAULT_LITHO_IMAGE_URL);
      setLocation(null);
      setErrors({
        name: false,
        phone: false,
        address: false,
        photo: false,
        location: false,
      });
    } catch (error) {
      console.error("Error saving data: ", error);
      alert("There was an error submitting your order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Language toggle component
  const LanguageToggle = ({ language, setLanguage }) => {
    const buttonStyle = (active) => ({
      marginLeft: "3px",
      padding: "2px 4px",
      backgroundColor: active ? "#007BFF" : "#ccc",
      color: active ? "#fff" : "#000",
      border: "none",
      borderRadius: "3px",
      cursor: "pointer",
      fontSize: "0.75rem",
    });
    return (
      <div className="language-toggle">
        <button style={buttonStyle(language === "en")} onClick={() => setLanguage("en")}>
          EN
        </button>
        <button style={buttonStyle(language === "ar")} onClick={() => setLanguage("ar")}>
          AR
        </button>
      </div>
    );
  };

  return (
    <div className="container">
      <LanguageToggle language={language} setLanguage={setLanguage} />
      <h2>{labels[language].pageTitle}</h2>
      <form onSubmit={handleSubmit}>
        <div className="photo-upload-container">
          <div className={`photo-box ${errors.photo ? "error" : ""}`} onClick={handleImageClick}>
            <img src={photoPreview} alt="Preview" />
            {!photo && <div className="upload-overlay">{labels[language].uploadPhoto}</div>}
          </div>
          <input type="file" accept="image/*" ref={fileInputRef} onChange={handlePhotoUpload} style={{ display: "none" }} />
          <div style={{ flex: 1 }}>
            <div className="select-group">
              <label>
                {labels[language].colorOption} <span className="required-asterisk">*</span>
              </label>
              <select value={colorOption} onChange={(e) => setColorOption(e.target.value)}>
                {language === "ar" ? (
                  <>
                    <option value="Mono">لون واحد</option>
                    <option value="Multi">الوان متعددة</option>
                  </>
                ) : (
                  <>
                    <option value="Mono">Mono</option>
                    <option value="Multi">Multi</option>
                  </>
                )}
              </select>
            </div>
            <div className="select-group">
              <label>
                {labels[language].frameColor} <span className="required-asterisk">*</span>
              </label>
              <select value={frameColor} onChange={(e) => setFrameColor(e.target.value)}>
                {language === "ar" ? (
                  <>
                    <option value="Black">اسود</option>
                    <option value="White">ابيض</option>
                  </>
                ) : (
                  <>
                    <option value="Black">Black</option>
                    <option value="White">White</option>
                  </>
                )}
              </select>
            </div>
          </div>
        </div>
        <label>
          {labels[language].pinLocation} <span className="required-asterisk">*</span>
        </label>
        <div className="map-container">
          <MapContainer
            center={location || [29.3759, 47.9774]}
            zoom={location ? 13 : 10}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer attribution="&copy; OpenStreetMap contributors" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {location && <Marker position={location} />}
            {location && <ChangeMapCenter location={location} />}
            <LocationSelector setLocation={setLocation} />
            <DetectLocationControl setLocation={setLocation} />
          </MapContainer>
        </div>
        <div className="form-group">
          <label>
            {labels[language].name} <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={labels[language].placeholderName}
            className={errors.name ? "error" : ""}
          />
        </div>
        <div className="form-group">
          <label>
            {labels[language].phone} <span className="required-asterisk">*</span>
          </label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={labels[language].placeholderPhone}
            className={errors.phone ? "error" : ""}
          />
        </div>
        <div className="form-group">
          <label>
            {labels[language].address} <span className="required-asterisk">*</span>
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={labels[language].placeholderAddress}
            className={errors.address ? "error" : ""}
          />
        </div>
        <div className="form-group">
          <label>{labels[language].note}</label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={labels[language].placeholderNote}
          />
        </div>
        <button type="submit" className="submit-button" disabled={isSubmitting} onClick={handleSubmit}>
          {isSubmitting ? "Submitting, please wait..." : labels[language].submit}
        </button>
      </form>
    </div>
  );
};

export default CustomerForm;
